import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ElementType,
  type ReactNode,
} from 'react';
import { Link } from 'react-router-dom';
import { Library, TrendingUp, Users, GraduationCap, Building2, Star, Crown, X, CheckCircle2 } from 'lucide-react';
import { useAuthStore } from '../stores/authStore';

export interface PremiumProviderItem {
  id: string;
  company_name: string | null;
  banner_image_url: string | null;
  address: string | null;
  tagline: string | null;
}

/* ------------------------------------------------------------------ */
/*  원본 premium-provider2.html 디자인 토큰                              */
/*  (기존 프로젝트 Navy/Orange 브랜드와 독립적으로 navy/blue/gold 사용)    */
/* ------------------------------------------------------------------ */

const NAVY = '#0f172a';
const BLUE = '#2563eb';
const BLUE_DARK = '#1d4ed8';
const GOLD = '#f59e0b';
const GRAY = '#64748b';
const LIGHT = '#f8fafc';
const LINE = '#e2e8f0';
const HEAD_FONT = '"Montserrat", "Noto Sans KR", sans-serif';
const SHADOW = '0 24px 70px rgba(15, 23, 42, 0.08)';

/* ------------------------------------------------------------------ */
/*  스크롤 리빌 래퍼                                                     */
/* ------------------------------------------------------------------ */

type RevealVariant = 'up' | 'left' | 'right' | 'scale' | 'fade';

interface RevealProps {
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
  variant?: RevealVariant;
  delay?: number;
  as?: ElementType;
}

const VARIANT_HIDDEN: Record<RevealVariant, string> = {
  up: 'opacity-0 translate-y-12',
  left: 'opacity-0 -translate-x-12',
  right: 'opacity-0 translate-x-12',
  scale: 'opacity-0 scale-90',
  fade: 'opacity-0',
};

function Reveal({ children, className = '', style, variant = 'up', delay = 0, as: Tag = 'div' }: RevealProps) {
  const ref = useRef<HTMLElement | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setVisible(true);
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.unobserve(el);
        }
      },
      { threshold: 0.15, rootMargin: '0px 0px -8% 0px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <Tag
      ref={ref as never}
      style={{ transitionDelay: `${delay}ms`, ...style, willChange: visible ? 'auto' : 'transform, opacity' }}
      className={`transition-all duration-[900ms] ease-[cubic-bezier(0.16,1,0.3,1)] ${
        visible ? 'opacity-100 translate-x-0 translate-y-0 scale-100' : VARIANT_HIDDEN[variant]
      } ${className}`}
    >
      {children}
    </Tag>
  );
}

/* ------------------------------------------------------------------ */
/*  데이터                                                              */
/* ------------------------------------------------------------------ */

const CRITERIA: { icon: ElementType; title: string; desc: string }[] = [
  { icon: Library, title: '교육 업력', desc: '기관의 교육 운영 기간과 누적된 교육 경험, 운영 안정성을 평가합니다.' },
  { icon: TrendingUp, title: '교육 실적', desc: '총 강의 회차, 누적 수강생 수, 정규 과정 운영 실적 등을 확인합니다.' },
  { icon: Users, title: '강사진 전문성', desc: '소속 강사의 수, 경력, 전문 분야, 교육 전달 역량을 종합적으로 검토합니다.' },
  { icon: GraduationCap, title: '교육 품질', desc: '커리큘럼의 완성도, 교육 깊이, 현장 적용 가능성을 평가합니다.' },
  { icon: Building2, title: '교육 환경', desc: '전용 교육 공간, 실습 환경, 정규 교육 과정 운영 체계를 확인합니다.' },
  { icon: Star, title: '브랜드 신뢰도', desc: '교육생 만족도, 업계 평판, 기관 운영 역량과 지속 가능성을 고려합니다.' },
];

const STEPS = [
  { n: 1, title: '인증 신청', desc: '기관 정보 및 교육 과정 제출' },
  { n: 2, title: '서류 검토', desc: '교육 이력과 운영 자료 확인' },
  { n: 3, title: '교육 평가', desc: '커리큘럼과 교육 환경 검토' },
  { n: 4, title: '전문 심사', desc: '전문위원 기준 평가' },
  { n: 5, title: '최종 승인', desc: '인증 등급 및 노출 확정' },
  { n: 6, title: '인증 유지', desc: '1년 단위 재평가 진행' },
];

const BENEFITS = [
  { title: '🏅 공식 인증 마크', desc: '기관 홈페이지, 홍보물, 교육 과정 안내에 활용 가능한 인증 마크를 제공합니다.' },
  { title: '🚀 교육 기관 홍보 지원', desc: '메인 배너 우선 노출로 교육기관의 인지도를 높입니다.' },
  { title: '📜 교육 관리 지원', desc: '우수 교육 기관 전용 페이지를 제공하여 업무 효율을 높입니다.' },
  { title: '🤝 브랜드 신뢰도 상승', desc: '검증된 교육 기관이라는 인식을 통해 교육생 모집 경쟁력을 높일 수 있습니다.' },
];

/* ------------------------------------------------------------------ */
/*  메인                                                                */
/* ------------------------------------------------------------------ */

interface PremiumProvidersProps {
  /** 실제 우수 인증 교육 기관 목록 (필라테스+피트니스 전체) */
  providers: PremiumProviderItem[];
}

export default function PremiumProviders({ providers }: PremiumProvidersProps) {
  const [inquiryOpen, setInquiryOpen] = useState(false);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const userRole = useAuthStore((s) => s.user?.role);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // 새로고침(마운트)마다 순서를 랜덤으로 섞음 — Fisher-Yates
  const shuffledProviders = useMemo(() => {
    const arr = [...providers];
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
  }, [providers]);

  // 교육기관(provider) 회원만 신청 모달 오픈, 그 외(비로그인/개인/기업 회원)는 안내 알림만 표시
  const openInquiry = () => {
    if (isAuthenticated && (userRole === 'provider' || userRole === 'admin')) {
      setInquiryOpen(true);
    } else {
      alert('우수 인증 교육 기관 신청은 교육기관 회원만 가능합니다. 교육기관 회원으로 로그인해주세요.');
    }
  };

  return (
    <div
      className="overflow-hidden"
      style={{ fontFamily: '"Noto Sans KR", sans-serif', color: NAVY, lineHeight: 1.6, background: '#fff' }}
    >
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@600;700;800&family=Noto+Sans+KR:wght@400;500;600;700;800&display=swap');
        @keyframes pp-blob { 0%,100% { transform: translate(0,0) scale(1); } 33% { transform: translate(20px,-26px) scale(1.08); } 66% { transform: translate(-16px,14px) scale(0.95); } }
        @keyframes pp-float { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-16px); } }
        @keyframes pp-rise { 0% { opacity:0; transform: translateY(26px); } 100% { opacity:1; transform: translateY(0); } }
        .pp-blob { animation: pp-blob 16s ease-in-out infinite; }
        .pp-float { animation: pp-float 8s ease-in-out infinite; }
        .pp-h1 { animation: pp-rise .8s cubic-bezier(0.16,1,0.3,1) .05s both; }
        .pp-h2 { animation: pp-rise .8s cubic-bezier(0.16,1,0.3,1) .2s both; }
        .pp-h3 { animation: pp-rise .8s cubic-bezier(0.16,1,0.3,1) .35s both; }
        .pp-h4 { animation: pp-rise .8s cubic-bezier(0.16,1,0.3,1) .5s both; }
        @media (prefers-reduced-motion: reduce) {
          .pp-blob,.pp-float,.pp-h1,.pp-h2,.pp-h3,.pp-h4 { animation: none !important; }
        }
      `}</style>

      {/* ============================ HERO ============================ */}
      <section
        className="relative overflow-hidden pb-24 pt-28"
        style={{
          background:
            'radial-gradient(circle at top right, rgba(37,99,235,0.16), transparent 35%), radial-gradient(circle at top left, rgba(245,158,11,0.16), transparent 30%), linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
        }}
      >
        <div className="mx-auto grid max-w-[1160px] items-center gap-[52px] lg:grid-cols-[1.08fr_0.92fr]">
          <div>
            <span
              className="pp-h1 mb-6 inline-flex items-center gap-2 rounded-full px-[15px] py-2.5 text-[13px] font-extrabold"
              style={{ background: 'rgba(37,99,235,0.08)', color: BLUE }}
            >
              🏆 STAMP FIT PREMIUM CERTIFIED PROVIDER
            </span>

            <h1
              className="pp-h2 mb-6 text-[42px] leading-[1.08] sm:text-[58px] lg:text-[74px]"
              style={{ letterSpacing: '-0.06em', fontWeight: 800 }}
            >
              대한민국 피트니스 교육의
              <br />
              새로운 <span style={{ color: BLUE }}>신뢰 기준</span>
            </h1>

            <p className="pp-h3 mb-8 max-w-[620px] text-[20px]" style={{ color: '#475569' }}>
              STAMP FIT는 교육 업력, 강사진, 커리큘럼, 교육 환경, 운영 역량을 종합적으로 평가하여 신뢰할 수
              있는 우수 교육 기관을 인증합니다.
            </p>

            <div className="pp-h4 flex flex-wrap gap-3.5">
              <a
                href="#pp-providers"
                className="inline-flex items-center justify-center rounded-full px-6 py-4 font-extrabold text-white transition-all duration-300 hover:-translate-y-0.5"
                style={{ background: BLUE, boxShadow: '0 16px 30px rgba(37,99,235,0.22)' }}
                onMouseEnter={(e) => (e.currentTarget.style.background = BLUE_DARK)}
                onMouseLeave={(e) => (e.currentTarget.style.background = BLUE)}
              >
                우수 인증 기관 보기
              </a>
              <button
                type="button"
                onClick={openInquiry}
                className="inline-flex items-center justify-center rounded-full bg-white px-6 py-4 font-extrabold transition-all duration-300 hover:-translate-y-0.5"
                style={{ color: NAVY, border: `1px solid ${LINE}` }}
              >
                인증 신청하기
              </button>
            </div>
          </div>

          {/* hero card */}
          <Reveal variant="scale" delay={150}>
            <div
              className="pp-float rounded-[36px] p-[34px]"
              style={{
                background: 'rgba(255,255,255,0.78)',
                border: '1px solid rgba(226,232,240,0.9)',
                boxShadow: SHADOW,
              }}
            >
              <div
                className="mb-[18px] rounded-[30px] p-8 text-white"
                style={{ background: 'linear-gradient(145deg, #0f172a, #1e293b)' }}
              >
                <div
                  className="mb-[22px] grid h-[78px] w-[78px] place-items-center rounded-full text-[34px]"
                  style={{ background: 'rgba(245,158,11,0.15)', border: '1px solid rgba(245,158,11,0.45)', color: GOLD }}
                >
                  ✓
                </div>
                <h3 className="mb-3.5 text-[28px] leading-tight" style={{ fontFamily: HEAD_FONT, fontWeight: 800 }}>
                  교육 여정을 더욱 가치 있게
                </h3>
                <p className="text-[15px]" style={{ color: '#cbd5e1' }}>
                  오랜 시간 쌓아온 교육에 대한 헌신과 노력을 더욱 빛나게 하겠습니다.
                </p>
              </div>

              <div className="grid grid-cols-3 gap-3.5">
                {[
                  ['1YR', '정기 재평가'],
                  ['2STEP', '정량-정성 평가'],
                  ['3BENEFIT', '교육 노출 & 홍보 지원'],
                ].map(([n, l]) => (
                  <div key={l} className="rounded-[22px] bg-white p-4" style={{ border: `1px solid ${LINE}` }}>
                    <strong className="mb-1 block text-[17px] leading-tight tracking-tight" style={{ fontFamily: HEAD_FONT }}>
                      {n}
                    </strong>
                    <span className="text-[12px] font-bold" style={{ color: GRAY }}>
                      {l}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ====================== CRITERIA ====================== */}
      <section className="py-[92px]">
        <div className="mx-auto max-w-[1160px]">
          <SectionHead
            eyebrow="EVALUATION CRITERIA"
            title="우수 인증 교육 기관 선정 기준"
            desc="단순한 교육 운영 여부가 아닌, 교육의 깊이와 지속 가능성, 그리고 피트니스 산업 내 신뢰도를 함께 평가합니다."
          />
          <div className="grid gap-[22px] sm:grid-cols-2 lg:grid-cols-3">
            {CRITERIA.map((c, i) => {
              const Icon = c.icon;
              return (
              <Reveal key={c.title} delay={i * 90} variant="up">
                <div
                  className="h-full rounded-[28px] bg-white p-[30px] transition-all duration-300 hover:-translate-y-1.5"
                  style={{ border: `1px solid ${LINE}`, boxShadow: '0 18px 50px rgba(15,23,42,0.04)' }}
                >
                  <div
                    className="mb-5 grid h-[52px] w-[52px] place-items-center rounded-[18px]"
                    style={{ background: 'rgba(37,99,235,0.08)' }}
                  >
                    <Icon className="h-6 w-6" style={{ color: BLUE }} />
                  </div>
                  <h3 className="mb-2.5 text-[22px]" style={{ letterSpacing: '-0.03em', fontWeight: 700 }}>
                    {c.title}
                  </h3>
                  <p className="text-[16px]" style={{ color: GRAY }}>
                    {c.desc}
                  </p>
                </div>
              </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ====================== PROCESS ====================== */}
      <section className="py-[92px]" style={{ background: LIGHT }}>
        <div className="mx-auto max-w-[1160px]">
          <SectionHead
            eyebrow="CERTIFICATION PROCESS"
            title="STAMP FIT 인증 절차"
            desc="신청부터 최종 승인까지 체계적인 절차를 통해 우수 인증 기관을 선정합니다."
          />
          <div className="grid grid-cols-2 gap-3.5 sm:grid-cols-3 lg:grid-cols-6">
            {STEPS.map((s, i) => (
              <Reveal key={s.n} delay={i * 80} variant="up">
                <div
                  className="h-full rounded-[24px] bg-white px-[18px] py-6 text-center transition-all duration-300 hover:-translate-y-1.5"
                  style={{ border: `1px solid ${LINE}` }}
                >
                  <div
                    className="mx-auto mb-3.5 grid h-[38px] w-[38px] place-items-center rounded-full text-white"
                    style={{ background: NAVY, fontFamily: HEAD_FONT, fontWeight: 800 }}
                  >
                    {s.n}
                  </div>
                  <h3 className="mb-2 text-[16px]" style={{ fontWeight: 700 }}>
                    {s.title}
                  </h3>
                  <p className="text-[13px]" style={{ color: GRAY }}>
                    {s.desc}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ====================== BENEFITS ====================== */}
      <section className="py-[92px]">
        <div className="mx-auto grid max-w-[1160px] items-center gap-[42px] lg:grid-cols-[0.9fr_1.1fr]">
          <Reveal variant="left">
            <div
              className="rounded-[36px] p-[42px] text-white"
              style={{ background: 'linear-gradient(145deg, #0f172a, #1e3a8a)', boxShadow: SHADOW }}
            >
              <div className="mb-3 text-[13px] font-extrabold" style={{ fontFamily: HEAD_FONT, color: '#fbbf24', letterSpacing: '0.12em' }}>
                PROVIDER BENEFITS
              </div>
              <h2 className="mb-[18px] text-[42px] leading-[1.18]" style={{ letterSpacing: '-0.05em', fontWeight: 700 }}>
                선정 기관이 받는 특별한 혜택
              </h2>
              <p className="text-[18px]" style={{ color: '#cbd5e1' }}>
                STAMP FIT 우수 인증 교육 기관은 공식 인증을 통해 교육생과 피트니스 시설 모두에게 더 높은
                신뢰를 전달할 수 있습니다.
              </p>
            </div>
          </Reveal>

          <div className="grid gap-[18px] sm:grid-cols-2">
            {BENEFITS.map((b, i) => (
              <Reveal key={b.title} delay={i * 90} variant="right">
                <div className="h-full rounded-[24px] bg-white p-6" style={{ border: `1px solid ${LINE}` }}>
                  <strong className="mb-2 block text-[18px]">{b.title}</strong>
                  <span className="text-[15px]" style={{ color: GRAY }}>
                    {b.desc}
                  </span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ====================== PROVIDERS ====================== */}
      <section id="pp-providers" className="py-[92px]" style={{ background: LIGHT }}>
        <div className="mx-auto max-w-[1160px]">
          <SectionHead
            eyebrow="FEATURED PROVIDERS"
            title="우수 인증 교육 기관"
            desc="현재 STAMP FIT에 입점한 우수 인증 교육기관입니다."
          />
          {shuffledProviders.length > 0 ? (
            <div
              className="flex gap-3 overflow-x-auto overflow-y-hidden px-1 py-3"
              style={{ scrollSnapType: 'x mandatory', WebkitOverflowScrolling: 'touch' }}
            >
              {shuffledProviders.map((provider, i) => (
                <Reveal key={provider.id} delay={i * 60} variant="fade" className="w-[300px] shrink-0" style={{ scrollSnapAlign: 'start' }}>
                  {/* 교육 메인의 우수 인증 교육 기관 카드와 동일 (클릭/자세히 보기 → 기관 페이지) */}
                  <Link
                    to={`/providers/${provider.id}`}
                    className="group block h-full rounded-xl bg-white shadow-[0_2px_15px_-3px_rgba(15,23,42,0.1)] transition-shadow hover:shadow-lg"
                    style={{ border: `1px solid ${LINE}` }}
                  >
                    <div className="relative">
                      <img
                        src={provider.banner_image_url || 'https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=400&h=300&fit=crop'}
                        alt={provider.company_name || '우수 인증 교육 기관'}
                        className="w-full block rounded-t-xl object-cover bg-gray-100"
                        style={{ aspectRatio: '16/9' }}
                      />
                      <div className="absolute right-2 top-2">
                        <span
                          className="inline-flex items-center gap-1 rounded-full px-2 py-1 text-[11px] font-semibold text-white"
                          style={{ background: BLUE }}
                        >
                          <Crown className="h-3 w-3" />
                          인증 우수
                        </span>
                      </div>
                    </div>
                    <div className="p-4">
                      <h3 className="mb-1 line-clamp-1 text-base font-bold" style={{ color: NAVY }}>
                        {provider.company_name || '우수 인증 교육 기관'}
                      </h3>
                      {provider.address && (
                        <p className="line-clamp-1 text-sm" style={{ color: GRAY }}>
                          {provider.address}
                        </p>
                      )}
                      {provider.tagline && (
                        <p className="mt-1.5 line-clamp-2 text-sm font-medium" style={{ color: BLUE }}>
                          {provider.tagline}
                        </p>
                      )}
                      <span className="mt-3 inline-block text-sm font-extrabold" style={{ color: BLUE }}>
                        자세히 보기 →
                      </span>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          ) : (
            <p className="text-center text-[16px]" style={{ color: GRAY }}>
              현재 등록된 우수 인증 교육 기관이 없습니다.
            </p>
          )}
          {shuffledProviders.length > 0 && (
            <div className="mt-2 text-center text-[14px] font-semibold" style={{ color: GRAY }}>
              ← 좌우로 스크롤하여 더 많은 인증 기관을 확인하세요 →
            </div>
          )}
        </div>
      </section>

      {/* ====================== CTA ====================== */}
      <section
        className="relative overflow-hidden py-[90px] text-center text-white"
        style={{
          background:
            'radial-gradient(circle at top right, rgba(245,158,11,0.28), transparent 30%), linear-gradient(135deg, #0f172a, #172554)',
        }}
      >
        <div className="pp-blob pointer-events-none absolute -right-10 -top-10 h-72 w-72 rounded-full" style={{ background: 'rgba(245,158,11,0.18)', filter: 'blur(40px)' }} />
        <Reveal className="relative mx-auto max-w-[1160px]">
          <h2 className="mb-5 text-[36px] leading-[1.16] sm:text-[48px] lg:text-[58px]" style={{ letterSpacing: '-0.06em', fontWeight: 700 }}>
            교육의 신뢰를 인증하다
          </h2>
          <p className="mx-auto mb-8 max-w-[720px] text-[19px]" style={{ color: '#cbd5e1' }}>
            귀 기관의 교육 품질과 전문성을 STAMP FIT를 통해 인증받아 보세요.
            <br />
            교육생에게는 더 나은 선택을, 기관에게는 더 큰 신뢰를 제공합니다.
          </p>
          <div className="flex flex-wrap justify-center gap-3.5">
            <button
              type="button"
              onClick={openInquiry}
              className="inline-flex items-center justify-center rounded-full px-6 py-4 font-extrabold text-white transition-all duration-300 hover:-translate-y-0.5"
              style={{ background: BLUE, boxShadow: '0 16px 30px rgba(37,99,235,0.22)' }}
            >
              우수 인증 교육 기관 신청하기
            </button>
            <a
              href="#"
              className="inline-flex items-center justify-center rounded-full bg-white px-6 py-4 font-extrabold transition-all duration-300 hover:-translate-y-0.5"
              style={{ color: NAVY }}
            >
              선정 기준 자세히 보기
            </a>
          </div>
        </Reveal>
      </section>

      {/* 인증 신청 모달 */}
      {inquiryOpen && <InquiryModal onClose={() => setInquiryOpen(false)} />}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  인증 신청 모달                                                       */
/* ------------------------------------------------------------------ */

function InquiryModal({ onClose }: { onClose: () => void }) {
  const [form, setForm] = useState({ name: '', companyName: '', phone: '', email: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const update = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!form.name.trim() || !form.companyName.trim() || !form.phone.trim() || !form.email.trim()) {
      setError('성명, 업체명, 전화번호, 이메일은 필수 입력입니다.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch('/api/v1/premium-inquiries', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.error || '신청 접수에 실패했습니다. 잠시 후 다시 시도해주세요.');
        return;
      }
      setDone(true);
    } catch {
      setError('네트워크 오류가 발생했습니다. 잠시 후 다시 시도해주세요.');
    } finally {
      setSubmitting(false);
    }
  };

  const labelCls = 'mb-1.5 block text-sm font-bold';
  const inputCls =
    'w-full rounded-xl border px-4 py-3 text-[15px] outline-none transition-colors focus:border-[#2563eb] focus:ring-2 focus:ring-[#2563eb]/20';

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4"
      style={{ fontFamily: '"Noto Sans KR", sans-serif' }}
      onClick={onClose}
    >
      <div
        className="max-h-[90vh] w-full max-w-[480px] overflow-y-auto rounded-[24px] bg-white p-7 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-1 flex items-start justify-between">
          <h3 className="text-[22px] font-extrabold" style={{ color: NAVY, fontFamily: HEAD_FONT }}>
            우수 인증 교육 기관 신청
          </h3>
          <button type="button" onClick={onClose} aria-label="닫기" className="rounded-lg p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700">
            <X className="h-5 w-5" />
          </button>
        </div>

        {done ? (
          <div className="py-8 text-center">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full" style={{ background: 'rgba(37,99,235,0.1)', color: BLUE }}>
              <CheckCircle2 className="h-8 w-8" />
            </div>
            <p className="text-[17px] font-bold" style={{ color: NAVY }}>
              인증 신청이 접수되었습니다.
            </p>
            <p className="mt-2 text-[15px]" style={{ color: GRAY }}>
              담당자가 확인 후 입력하신 연락처로 안내드리겠습니다.
            </p>
            <button
              type="button"
              onClick={onClose}
              className="mt-6 inline-flex items-center justify-center rounded-full px-6 py-3 font-extrabold text-white transition-transform hover:-translate-y-0.5"
              style={{ background: BLUE }}
            >
              확인
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="mt-4 space-y-4">
            <p className="text-[14px]" style={{ color: GRAY }}>
              아래 정보를 입력해 주시면 우수 인증 절차를 안내해 드립니다.
            </p>

            <div>
              <label className={labelCls} style={{ color: NAVY }}>성명 *</label>
              <input className={inputCls} style={{ borderColor: LINE }} value={form.name} onChange={update('name')} placeholder="홍길동" maxLength={100} />
            </div>
            <div>
              <label className={labelCls} style={{ color: NAVY }}>업체명 *</label>
              <input className={inputCls} style={{ borderColor: LINE }} value={form.companyName} onChange={update('companyName')} placeholder="○○ 피트니스 아카데미" maxLength={200} />
            </div>
            <div>
              <label className={labelCls} style={{ color: NAVY }}>전화번호 *</label>
              <input className={inputCls} style={{ borderColor: LINE }} value={form.phone} onChange={update('phone')} placeholder="010-0000-0000" maxLength={50} />
            </div>
            <div>
              <label className={labelCls} style={{ color: NAVY }}>이메일 *</label>
              <input className={inputCls} style={{ borderColor: LINE }} type="email" value={form.email} onChange={update('email')} placeholder="example@company.com" maxLength={200} />
            </div>
            <div>
              <label className={labelCls} style={{ color: NAVY }}>기타 문의사항</label>
              <textarea className={`${inputCls} resize-none`} style={{ borderColor: LINE }} rows={4} value={form.message} onChange={update('message')} placeholder="교육 분야, 운영 현황 등 자유롭게 작성해 주세요." maxLength={2000} />
            </div>

            {error && (
              <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600">{error}</p>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="w-full rounded-full px-6 py-3.5 font-extrabold text-white transition-all hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-60"
              style={{ background: BLUE, boxShadow: '0 16px 30px rgba(37,99,235,0.22)' }}
            >
              {submitting ? '접수 중...' : '인증 신청하기'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  SectionHead                                                        */
/* ------------------------------------------------------------------ */

function SectionHead({ eyebrow, title, desc }: { eyebrow: string; title: string; desc: string }) {
  return (
    <Reveal className="mx-auto mb-[54px] max-w-[720px] text-center">
      <div className="mb-3 text-[13px] font-extrabold" style={{ fontFamily: HEAD_FONT, color: BLUE, letterSpacing: '0.12em' }}>
        {eyebrow}
      </div>
      <h2 className="mb-[18px] text-[32px] leading-[1.18] sm:text-[40px] lg:text-[50px]" style={{ letterSpacing: '-0.05em', fontWeight: 700 }}>
        {title}
      </h2>
      <p className="text-[18px]" style={{ color: GRAY }}>
        {desc}
      </p>
    </Reveal>
  );
}
